import type { CSSProperties } from 'react';

// #2504 (RZP.C1) — Terms of Service and Privacy Policy, reachable from inside
// the authenticated app.
//
// AVR decision D1: the app LINKS to the marketing-hosted canonical pages; it does
// NOT serve its own copies. LexLabs revise that text, and a duplicate in apps/web
// would mean every revision has to land in two codebases. So this component emits
// links and nothing else — no legal copy lives here (AC2).
//
// Rendered on THREE surfaces, and that is not redundancy:
//   - SettingsPage          — tenant users (anyone with a tenantId claim)
//   - ClientDashboard       — client stakeholders
//   - ProjectMemberDashboard — project_members-only accounts
//
// A stakeholder account has no tenantId claim, and ProtectedRoute redirects such
// an account away from any route outside CLIENT_LANDING_ROUTES — a set that
// contains /client/dashboard and /member/dashboard but NOT /settings. So a
// Settings-only link is unreachable for exactly the users the compliance intent
// is about. The union of the three surfaces is what satisfies AC1, not any one of
// them. See ProtectedRoute.tsx:22 and the #2504 recon.
//
// .html URLs, deliberately: clean URLs 404 on blueprintsdlc.com — measured in
// #2689, and CLAUDE.md §10 carries the same warning (its "403" is the figure
// #2689 corrects). /terms.html and /privacy.html resolve; /terms and /privacy
// do not.
//
// Scope: Terms and Privacy only. refunds.html and cookies.html are #2506's pages
// and are deliberately not linked here until that story merges — extending this
// component is a follow-up, not part of #2504.
//
// NOT related to /terms/partner (PartnerTermsPage), which is a different document
// under a separate version constant (AC3).

const TERMS_URL   = 'https://blueprintsdlc.com/terms.html';
const PRIVACY_URL = 'https://blueprintsdlc.com/privacy.html';

const containerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'center',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--text-secondary)',
};

const linkStyle: CSSProperties = { color: 'var(--primary)', textDecoration: 'underline' };

interface Props {
  /**
   * Optional style overrides (typically spacing) for the host surface. Every
   * host supplies its own margins: this component assumes NO shared chrome,
   * because ProjectMemberDashboard is deliberately not wrapped in
   * ProjectLayout/Sidebar and ClientDashboard renders no Sidebar either.
   */
  style?: CSSProperties;
}

export default function LegalLinks({ style }: Props) {
  return (
    <div
      data-testid="legal-links"
      style={{ ...containerStyle, ...style }}
    >
      <a
        href={TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
        data-testid="legal-links-terms"
      >
        Terms of Service
      </a>
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
        data-testid="legal-links-privacy"
      >
        Privacy Policy
      </a>
    </div>
  );
}
